import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Activity, ShieldCheck, Users, Zap, Award, RefreshCw, User } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const ACTION_ICONS = {
  welcome: { icon: Award, color: 'text-purple-400', bg: 'bg-purple-500/10' },
  new_creator: { icon: Users, color: 'text-blue-400', bg: 'bg-blue-500/10' },
  link_protect: { icon: ShieldCheck, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  admin_adjust: { icon: Zap, color: 'text-red-400', bg: 'bg-red-500/10' },
  daily: { icon: Activity, color: 'text-amber-400', bg: 'bg-amber-500/10' },
};

export default function AdminActivity() {
  const { token, API } = useAuth();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const fetchActivity = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/activity?limit=100`, { headers: { Authorization: `Bearer ${token}` } });
      setActivities(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [API, token]);

  useEffect(() => { fetchActivity(); }, [fetchActivity]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchActivity, 5000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchActivity]);

  const getActionMeta = (action) => ACTION_ICONS[action] || ACTION_ICONS.daily;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="flex items-center justify-between">
        <p className="text-xs text-zinc-500">{activities.length} recent actions</p>
        <div className="flex items-center gap-3">
          <button
            data-testid="admin-activity-refresh"
            onClick={fetchActivity}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs bg-white/5 text-zinc-400 hover:bg-white/10 transition-colors"
          >
            <RefreshCw className="w-3 h-3" />
            Refresh
          </button>
          <button
            data-testid="admin-activity-auto-refresh"
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs transition-colors ${
              autoRefresh
                ? 'bg-amber-500/10 text-amber-500 border border-amber-500/20'
                : 'bg-white/5 text-zinc-400 hover:bg-white/10'
            }`}
          >
            <div className={`w-1.5 h-1.5 rounded-full ${autoRefresh ? 'bg-emerald-400 animate-pulse' : 'bg-zinc-600'}`} />
            Live
          </button>
        </div>
      </div>

      {/* Activity Feed */}
      <div className="space-y-2">
        {activities.map((a, i) => {
          const meta = getActionMeta(a.action);
          const Icon = meta.icon;
          const timeAgo = getTimeAgo(a.created_at);

          return (
            <motion.div
              key={a.id}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: Math.min(i * 0.02, 0.5) }}
              className="glass-card rounded-xl px-5 py-4 flex items-center gap-4 hover:border-white/10 transition-colors"
              data-testid={`admin-activity-item-${i}`}
            >
              <div className={`w-9 h-9 rounded-xl ${meta.bg} flex items-center justify-center flex-shrink-0`}>
                <Icon className={`w-4 h-4 ${meta.color}`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white">{a.description}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <User className="w-3 h-3 text-zinc-600" />
                  <span className="text-[10px] text-zinc-500">{a.user_name}</span>
                  <span className="text-[10px] text-zinc-700">{a.user_email}</span>
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <span className={`text-sm font-mono font-bold ${a.points >= 0 ? 'text-amber-500' : 'text-red-400'}`}>
                  {a.points >= 0 ? '+' : ''}{a.points}
                </span>
                <p className="text-[10px] text-zinc-600 mt-0.5">{timeAgo}</p>
              </div>
            </motion.div>
          );
        })}

        {activities.length === 0 && (
          <div className="text-center py-16">
            <Activity className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
            <p className="text-sm text-zinc-500">No activity yet</p>
            <p className="text-xs text-zinc-600">Activity will appear here as users interact with the extension</p>
          </div>
        )}
      </div>
    </div>
  );
}

function getTimeAgo(dateStr) {
  const now = new Date();
  const then = new Date(dateStr);
  const diffMs = now - then;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDays = Math.floor(diffHr / 24);
  return `${diffDays}d ago`;
}
